import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Grid,
    Typography,
    Button,
    Box,
    Card,
    CardMedia,
    CardContent,
    CircularProgress,
} from '@mui/material';
import { useCart } from '../../contexts/CartContext';
import { Product } from '../../types';
import ReservationForm from '../Reservation/ReservationForm';
import axios from 'axios';
import ProductCustomDescriptionBox from '../ProductCustomDescriptionBox';

const ProductDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [product, setProduct] = useState<Product | null>(null);
    const [loading, setLoading] = useState(true);
    const [showReservationForm, setShowReservationForm] = useState(false);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await axios.get(`/api/products/${id}`);
                setProduct(response.data);
            } catch (error) {
                console.error('Erreur lors de la récupération du produit:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [id]);

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!product) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Produit non trouvé
                </Typography>
                <Button variant="contained" onClick={() => navigate('/categories')}>
                    Retour aux catégories
                </Button>
            </Container>
        );
    }

    const handleReservationSuccess = () => {
        navigate('/payment/success');
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {!showReservationForm ? (
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardMedia
                                component="img"
                                height="400"
                                image={product.imageUrl}
                                alt={product.name}
                                onError={(e) => {
                                    console.error(`Erreur de chargement de l'image pour ${product.name}`);
                                    console.error('URL de l\'image:', product.imageUrl);
                                    // Image de secours en cas d'erreur
                                    e.currentTarget.src = '/uploads/products/logo.svg';
                                }}
                            />
                        </Card>

                        {/* Description personnalisée du produit sous l'image */}
                        <Box sx={{ mt: 3 }}>
                            <ProductCustomDescriptionBox
                                customDescription={product.customDescription}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            {product.name}
                        </Typography>
                        <Typography variant="h5" color="primary" gutterBottom>
                            {product.price}€
                        </Typography>
                        <Box
                            sx={{
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                padding: 3,
                                marginBottom: 3,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            <Typography variant="body1" paragraph sx={{ margin: 0 }}>
                                {product.description}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Informations pratiques :
                            </Typography>
                            <Typography>Durée : {product.details.duration}</Typography>
                            <Typography>Age minimum : {product.details.minAge} ans</Typography>
                            <Typography>Age maximum : {product.details.maxAge} ans</Typography>
                            <Typography>Profondeur maximale : {product.details.maxDepth}m</Typography>
                        </Box>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Inclus :
                            </Typography>
                            <ul>
                                {product.details.included.map((item, index) => (
                                    <li key={index}>
                                        <Typography>{item}</Typography>
                                    </li>
                                ))}
                            </ul>
                        </Box>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Prérequis :
                            </Typography>
                            <ul>
                                {product.details.prerequisites.map((req, index) => (
                                    <li key={index}>
                                        <Typography>{req}</Typography>
                                    </li>
                                ))}
                            </ul>
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => setShowReservationForm(true)}
                            sx={{
                                mr: 2,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                backgroundColor: '#1976d2',
                                border: '2px solid #1976d2',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#1565c0',
                                    border: '2px solid #1565c0',
                                    color: 'white'
                                }
                            }}
                        >
                            Réserver maintenant
                        </Button>
                    </Grid>
                </Grid>
            ) : (
                <ReservationForm />
            )}
        </Container>
    );
};

export default ProductDetail;